// ─────────────────────────────────────────────────────────────────────────────
// buildAnalytics.js — the payload → chart-data transforms for this page. Kept
// out of the hook so each one can be read (and reasoned about) on its own; the
// hook stays a description of what is fetched and what falls back to what.
// ─────────────────────────────────────────────────────────────────────────────
import { buildBandwidthData } from "../../services/dashboardService";

/**
 * Derives daily traffic volume (GB) from the SAME per-day average throughput
 * (entry_throughput_bps) that the Dashboard's "Network Bandwidth" chart plots
 * over its 7-day range, so the two charts stay in lockstep — same daily shape,
 * expressed as data (GB) instead of rate (bps). Each day's average throughput
 * (bits/sec) is projected across a full day and converted to GB:
 *   GB = avg_bps × 86400 s ÷ 8 bits/byte ÷ 1e9
 *
 * @returns {{label: string, val: number}[] | null} at least 2 points, or null
 */
export function buildTrafficChartData(trends) {
  const bw = buildBandwidthData(trends, "Last 7 Days");
  if (!bw || bw.values.length < 2) return null;
  const BPS_TO_GB_PER_DAY = 86400 / 8 / 1_000_000_000; // avg bps → GB over one day
  return bw.labels.map((label, i) => ({
    label,
    val: +((bw.values[i] * BPS_TO_GB_PER_DAY).toFixed(1)),
  }));
}

/**
 * traffic_by_region → the top 5 regions by bytes, descending. The card scales
 * its bars against the first entry, so the sort order is load-bearing.
 * @returns {{name: string, gb: number}[] | null}
 */
export function buildRegionsData(trafficByRegion) {
  if (!trafficByRegion?.length) return null;
  return [...trafficByRegion]
    .sort((a, b) => (b.total_bytes ?? 0) - (a.total_bytes ?? 0))
    .slice(0, 5)
    .map((r) => ({
      name: r.region || r.country || "Unknown",
      gb:   +(((r.total_bytes ?? 0) / 1_000_000_000).toFixed(1)),
    }));
}

/** user-activity trend → LineChart points (same shape as the traffic series). */
export function buildActivityChartData(trend = [], dayLabel) {
  return trend.map((p) => ({
    label: dayLabel(p.date),
    val:   p.active_users ?? 0,
  }));
}

/** node-usage-by-region → BarChart bars, capped at the 6 the card can show. */
export function buildNodeUsageData(regions = []) {
  return regions.slice(0, 6).map((r) => ({
    label: r.region,
    val:   Math.round(r.utilization),
  }));
}

/** top-nodes → the "Most Used Nodes" table rows. */
export function buildTopNodesRows(nodes = []) {
  return nodes.map((n) => ({
    id:     n.node_id,
    region: n.region ?? "—",
    // A node the backend has no trust reading for shows "—", not 0 — a 0 here
    // would read as "measured, and terrible".
    trust:  n.trust_score !== null && n.trust_score !== undefined
              ? `${Number(n.trust_score).toFixed(2)}%`
              : "—",
    users:  n.active_circuits ?? 0,
    load:   Math.round(n.load_percent ?? 0),
  }));
}
